import { Auth } from 'aws-amplify'

import { setCurrentUserIdentityForAnalytics } from '@/analytics'
import user_plan_store from '@/store/user/modules/user_plan_store'
import AuthActions from '@/utils/actions/auth_actions'
import { SMAGableExtensions } from '@/xstore/utils/smagable'
import { ToggleableExtensions } from '@/xstore/utils/toggleable'
import { ResettableExtensions } from '@/xstore/utils/resettable'

let getAuthCredentialsPromise

function createRefreshCredentials(commit) {
  return () => {
    getAuthCredentialsPromise = Auth.currentUserCredentials()
      .then(creds => {
        commit('SET_CACHED_AUTH_CREDENTIALS', creds)
        getAuthCredentialsPromise = undefined
        return creds
      })
      .catch(error => {
        getAuthCredentialsPromise = undefined // Clear the promise on error
        throw error // Re-throw to handle it in the retry logic
      })

    return getAuthCredentialsPromise
  }
}

async function getAuthCredentialsWithRetry(
  { commit, getters },
  options,
  retryCount = 0
) {
  const refreshCredentials = createRefreshCredentials(commit)
  try {
    return await refreshCredentials()
  } catch (error) {
    if (retryCount < 3) {
      // Maximum 3 retries
      console.log(`Token refresh attempt ${retryCount + 1} failed, retrying...`)
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))) // Exponential backoff
      return getAuthCredentialsWithRetry(
        { commit, getters },
        options,
        retryCount + 1
      )
    }
    console.error('Token refresh failed after 3 attempts:', error)
    throw error
  }
}

async function getUserJwtTokenWithRetry(retryCount = 0) {
  try {
    const user = await Auth.currentAuthenticatedUser()
    return user.getSignInUserSession().getIdToken().getJwtToken()
  } catch (error) {
    // Check if it's a refresh token expiration
    if (error.message.includes('Refresh Token has expired')) {
      // Use the correct sign out method
      await AuthActions.signOut()
      throw error
    }

    // Only retry for other types of errors
    if (retryCount < 3) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)))
      return getUserJwtTokenWithRetry(retryCount + 1)  
    }
    console.error('User JWT Token failed after 3 attempts:', error)
    throw error
  }
}

export default ({ useExtension }) => {
  {
    const state = { current_cognito_user: undefined }

    useExtension(SMAGableExtensions.SMAGable, { state })
    useExtension(ToggleableExtensions.Loadable, { state })
  }

  {
    const state = { cached_auth_credentials: undefined }

    useExtension(SMAGableExtensions.SMGable, { state })
  }

  useExtension(ResettableExtensions.Default)

  return {
    modules: {
      plan: user_plan_store,
    },
    actions: {
      // Custom reset action that also clears global variables
      RESET: ({ commit }) => {
        // Clear global variables
        getAuthCredentialsPromise = undefined
        
        // Clear cached credentials explicitly
        commit('SET_CACHED_AUTH_CREDENTIALS', undefined)
        commit('SET_CURRENT_COGNITO_USER', undefined)
        
        // Clear analytics
        setCurrentUserIdentityForAnalytics(null)
      },
      authUser: (() => {
        let plansFetched = false
        let currentSubscriptionFetched = false

        return async ({ dispatch, toggleIsLoading, commit }) => {
          await toggleIsLoading(
            new Promise((resolve, reject) =>
              Auth.currentAuthenticatedUser()
                .then(() => Auth.currentUserInfo())
                .then(
                  cognito_user_data => {
                    setCurrentUserIdentityForAnalytics({
                      identityId: cognito_user_data.id,
                      userSub: cognito_user_data.attributes.sub,
                      email: cognito_user_data.attributes.email,
                    })

                    return dispatch(
                      'setCurrentCognitoUser',
                      cognito_user_data
                    ).then(() => resolve(cognito_user_data))
                  },
                  e => {
                    setCurrentUserIdentityForAnalytics(null)

                    return Promise.all([
                      dispatch('setCurrentCognitoUser'),
                      commit('SET_CACHED_AUTH_CREDENTIALS'),
                    ]).then(() => reject(e))
                  }
                )
            ),
            'current_cognito_user'
          )

          if (!plansFetched) {
            dispatch('plan/fetchPlans')
            plansFetched = true
          }

          if (!currentSubscriptionFetched) {
            dispatch('plan/current_subscription/fetchCurrentSubscription')
            currentSubscriptionFetched = true
          }

          return dispatch('getAuthCredentials')
        }
      })(),
      getUserJwtToken: () => getUserJwtTokenWithRetry(),
      getAuthCredentials: async ({ commit, getters }, options) => {
        const { forceRefresh } = options || {}

        if (getAuthCredentialsPromise) {
          return getAuthCredentialsPromise
        }

        if (!forceRefresh) {
          let creds = getters.getCachedAuthCredentials
          let timeAfter5mins = (() => {
            const timeNow = new Date()
            timeNow.setMinutes(timeNow.getMinutes() + 5)
            return timeNow
          })()
          if (creds && creds.expiration > timeAfter5mins) {
            return creds
          }
        }

        return getAuthCredentialsWithRetry({ commit, getters }, options)
      },
    },
  }
}
